import { ProfileHeader } from "@components/Account/You/ProfileHeader";
import { StatsTrio } from "@components/Account/You/StatsTrio";
import { TopArtistsList } from "@components/Account/You/TopArtistsList";
import LBox from "@components/LBox";
import LText from "@components/LText";
import type { UserProfileModalProps } from "@components/modals/UserProfileModal/types";
import { useUserStats } from "@hooks/profile/useUserStats";
import { BottomSheetHandle } from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { APP_MODAL_CORNER_RADIUS } from "@utils/appModal/constants";
import { forwardRef } from "react";
import {
  ActivityIndicator,
  Dimensions,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";

const MAX_PROFILE_HEIGHT = Math.min(
  Dimensions.get("window").height * 0.76,
  680,
);

const DarkSheetSurface = () => (
  <View style={[StyleSheet.absoluteFill, styles.darkSurface]} />
);

const UserProfileModalSWM = ({
  userId,
  name,
  image,
}: UserProfileModalProps) => {
  const { stats, thisMonth, isLoadingStats } = useUserStats(userId);
  const bottomSheetManager = useBottomSheetManager();

  if (isLoadingStats) {
    return (
      <LBox pb="l1">
        <BottomSheetHandle backgroundColor="whiteTranslucent70" />
        <LBox
          alignItems="center"
          justifyContent="center"
          minHeight={160}
          px="s4"
        >
          <ActivityIndicator color="#FFFFFF" />
        </LBox>
      </LBox>
    );
  }

  if (!stats) {
    return (
      <LBox pb="l1">
        <BottomSheetHandle backgroundColor="whiteTranslucent70" />
        <LBox alignItems="center" minHeight={120} px="s4" py="l1">
          <LText color="subText">No stats yet</LText>
        </LBox>
      </LBox>
    );
  }

  return (
    <LBox pb="l1">
      <BottomSheetHandle backgroundColor="whiteTranslucent70" />
      <ScrollView
        bounces
        contentContainerStyle={{ paddingBottom: 24 }}
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: MAX_PROFILE_HEIGHT }}
      >
        <LBox p="s4" pb="l3" gap="s4">
          <LBox padding="s3" borderRadius="l" gap="m1" style={styles.card}>
            <ProfileHeader
              userDisplayName={name}
              userAvatarUrl={image ?? undefined}
              editable={false}
            />

            <LBox height={1} style={styles.divider} />

            <StatsTrio
              totalStreams={stats.totalStreams ?? 0}
              accesses={stats.accesses ?? 0}
            />
          </LBox>

          {stats.topArtists && stats.topArtists.length > 0 ? (
            <LBox padding="s4" borderRadius="l" style={styles.card}>
              <TopArtistsList
                artists={stats.topArtists}
                thisMonthArtists={thisMonth?.topArtists}
                maxVisible={3}
                userId={userId}
                isInModal
                showBreakdown={false}
                onCloseModal={bottomSheetManager.closeAll}
              />
            </LBox>
          ) : null}
        </LBox>
      </ScrollView>
    </LBox>
  );
};

export const UserProfileModalSWMSheet = forwardRef<
  SheetAdapterRef,
  UserProfileModalProps
>(function UserProfileModalSWMSheet({ userId, name, image }, ref) {
  return (
    <AppModalSheet ref={ref} showHandle={false} surface={<DarkSheetSurface />}>
      <UserProfileModalSWM userId={userId} name={name} image={image} />
    </AppModalSheet>
  );
});

export default UserProfileModalSWM;

const styles = StyleSheet.create({
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.05)",
  },
  darkSurface: {
    backgroundColor: "#17171C",
    borderTopLeftRadius: APP_MODAL_CORNER_RADIUS,
    borderTopRightRadius: APP_MODAL_CORNER_RADIUS,
    overflow: "hidden",
  },
  divider: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
});
